import { useState, type FC, type ReactNode } from "react";
import { View } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import { Button, Text } from "@visioni/a3s-ui/components";
import "@visioni/a3s-ui/theme";
import type { ReportEmailDeliveryFrequency } from "../../../client-messages";
import type { ReportEmailDeliveryFormModel } from "../../hooks/useReportEmailDeliveryForm";
import { reportEmailDeliverySummary } from "../../report-email-delivery-summary";
import type { ReportUpdateTimeContext } from "../../report-update-time";
import { FrequencyField } from "../FrequencyField";
import { ReportEmailDeliveryExplainer } from "../ReportEmailDeliveryExplainer";
import { ScheduleGroup } from "../ScheduleGroup";
import { SentToField } from "../SentToField";
import { WeekdayField } from "../WeekdayField";

interface ReportEmailDeliveryFormCommonProps extends ReportUpdateTimeContext {
  /** The site this report email delivery is for. */
  readonly siteName: string;
  /** The signed-in user's email, which the report is sent to. */
  readonly email: string;
  /** From `useReportEmailDeliveryForm`. */
  readonly form: ReportEmailDeliveryFormModel;
  /** A start or a save is in flight: the form is disabled and its button shows loading. */
  readonly isSubmitting: boolean;
  /** Shown above the explainer, e.g. `ReportEmailAccessBanner`. */
  readonly accessBanner?: ReactNode;
}

/** No delivery yet: the form sets one up. */
export interface NewReportEmailDeliveryFormProps extends ReportEmailDeliveryFormCommonProps {
  readonly mode: "new";
  /** Called with exactly the PUT body. */
  readonly onStart: (value: ReportEmailDeliveryFrequency) => void;
}

/** A saved delivery: the form changes or stops it. */
export interface ActiveReportEmailDeliveryFormProps extends ReportEmailDeliveryFormCommonProps {
  readonly mode: "active";
  /** What is saved now, summarised on the "Active" line. */
  readonly saved: ReportEmailDeliveryFrequency;
  /** Called with exactly the PUT body. */
  readonly onSave: (value: ReportEmailDeliveryFrequency) => void;
  /** Called once the user confirms the stop. */
  readonly onStop: () => void;
  /** A stop is in flight: the form is disabled and the confirm button shows loading. */
  readonly isStopping: boolean;
}

export type ReportEmailDeliveryFormProps = NewReportEmailDeliveryFormProps | ActiveReportEmailDeliveryFormProps;

/** The body of the page that sets up, changes or stops a RoadSense report email delivery for one site. */
export const ReportEmailDeliveryForm: FC<ReportEmailDeliveryFormProps> = (props) => {
  const { siteName, email, form, isSubmitting, accessBanner, now, viewerTimeZone } = props;
  const disabled = isSubmitting || (props.mode === "active" && props.isStopping);

  return (
    <View style={styles.form}>
      <View style={styles.header}>
        <Text textStyle="lg/semibold" color="text" role="heading">
          Schedule Email Reports
        </Text>
        <Text textStyle="sm/medium" color="text">
          {siteName}
        </Text>
        <Text textStyle="sm/regular" color="textMuted">
          A clear picture of vehicle flow and traffic insights. Get your Excel report emailed to you, daily or weekly.
        </Text>
      </View>
      {props.mode === "active" && (
        <View style={styles.activeLine}>
          <Text textStyle="sm/semibold" color="text">
            Active
          </Text>
          <Text textStyle="sm/regular" color="textMuted">
            {reportEmailDeliverySummary(props.saved, { now, viewerTimeZone })}
          </Text>
        </View>
      )}
      {accessBanner}
      <ReportEmailDeliveryExplainer />
      <ScheduleGroup title="Schedule">
        <FrequencyField
          value={form.frequency}
          onChange={form.setFrequency}
          disabled={disabled}
          weekday={form.weekday}
          now={now}
          viewerTimeZone={viewerTimeZone}
        />
        {form.frequency === "weekly" && (
          <WeekdayField value={form.weekday} onChange={form.setWeekday} disabled={disabled} />
        )}
        <SentToField email={email} />
      </ScheduleGroup>
      {props.mode === "new" ?
        <View style={styles.actions}>
          <Button
            variant="primary"
            label="Start my reports"
            loading={isSubmitting}
            disabled={disabled}
            onPress={() => props.onStart(form.value)}
          />
        </View>
      : <ActiveActions
          isDirty={form.isDirty}
          isSubmitting={isSubmitting}
          isStopping={props.isStopping}
          disabled={disabled}
          onSave={() => props.onSave(form.value)}
          onStop={props.onStop}
        />
      }
    </View>
  );
};

interface ActiveActionsProps {
  readonly isDirty: boolean;
  readonly isSubmitting: boolean;
  readonly isStopping: boolean;
  readonly disabled: boolean;
  readonly onSave: () => void;
  readonly onStop: () => void;
}

/** Save changes and Stop reports, with Stop's inline confirm step. */
const ActiveActions: FC<ActiveActionsProps> = ({ isDirty, isSubmitting, isStopping, disabled, onSave, onStop }) => {
  // Local UI state: the confirm step lives and dies with the active form.
  const [isConfirmingStop, setIsConfirmingStop] = useState(false);

  if (isConfirmingStop || isStopping) {
    return (
      <View style={styles.confirm}>
        <Text textStyle="sm/semibold" color="text">
          Stop emailing this report?
        </Text>
        <View style={styles.actions}>
          <Button variant="secondary" label="Keep it" disabled={disabled} onPress={() => setIsConfirmingStop(false)} />
          <Button
            variant="primary"
            variantColor="danger"
            label="Stop reports"
            loading={isStopping}
            disabled={disabled}
            onPress={onStop}
          />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.actions}>
      <Button
        variant="primary"
        label="Save changes"
        loading={isSubmitting}
        disabled={disabled || !isDirty}
        onPress={onSave}
      />
      <Button
        variant="secondary"
        variantColor="danger"
        label="Stop reports"
        disabled={disabled}
        onPress={() => setIsConfirmingStop(true)}
      />
    </View>
  );
};

const styles = StyleSheet.create((theme) => ({
  form: {
    gap: theme.spacing["3xl"],
  },
  header: {
    gap: theme.spacing.xs,
  },
  activeLine: {
    flexDirection: "row",
    flexWrap: "wrap",
    columnGap: theme.spacing.md,
    rowGap: theme.spacing.xxs,
  },
  actions: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: theme.spacing.md,
  },
  confirm: {
    gap: theme.spacing.md,
  },
}));
